import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Search } from "lucide-react";
import { api, formatINR, formatApiErrorDetail, formatCompactNumber } from "@/lib/api";

export default function AdminInventory() {
  const [data, setData] = useState({ total: 0, items: [] });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  async function load() {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (q) params.q = q;
      const { data: res } = await api.get("/public/inventory", { params });
      setData(res);
    } catch {
      setData({ total: 0, items: [] });
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, [q]);

  async function del(id) {
    if (!window.confirm("Delete this placement?")) return;
    try {
      await api.delete(`/admin/inventory/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }

  return (
    <div data-testid="admin-inventory">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="overline">Console · Inventory</div>
          <h1 className="mt-2 font-display text-3xl lg:text-4xl tracking-tighter">Inventory · {data.total}</h1>
          <p className="mt-1 text-neutral-400 text-sm">All placements across the marketplace.</p>
        </div>
        <div className="relative max-w-xs w-full">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search mall / format / city" className="pl-9" />
        </div>
      </div>

      <div className="mt-8 surface overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-white/10 text-left text-[11px] uppercase tracking-[0.18em] text-neutral-500">
            <tr>
              <th className="p-4">Placement</th>
              <th className="p-4">Mall</th>
              <th className="p-4">City</th>
              <th className="p-4">Category</th>
              <th className="p-4">Footfall</th>
              <th className="p-4">Vis.</th>
              <th className="p-4">Price / wk</th>
              <th className="p-4">Status</th>
              <th className="p-4 w-12"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="9" className="p-8 text-center text-neutral-500">Loading…</td></tr>
            ) : data.items.length === 0 ? (
              <tr><td colSpan="9" className="p-8 text-center text-neutral-500">No matches.</td></tr>
            ) : data.items.map((i) => (
              <tr key={i.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`inv-row-${i.id}`}>
                <td className="p-4">
                  <div className="font-medium">{i.format}</div>
                  <div className="text-xs text-neutral-500">{i.placement}</div>
                </td>
                <td className="p-4 text-neutral-300">{i.mall_name}</td>
                <td className="p-4 text-neutral-400">{i.city}</td>
                <td className="p-4">
                  <span className="text-[10px] uppercase tracking-[0.18em] bg-white/5 border border-white/10 px-2 py-1">{i.format_category}</span>
                </td>
                <td className="p-4 text-neutral-300 font-mono text-xs">{formatCompactNumber(i.monthly_footfall)}</td>
                <td className="p-4 text-neutral-300 font-mono text-xs">{i.visibility_score}</td>
                <td className="p-4 text-amber-500 whitespace-nowrap">{formatINR(i.price_per_week)}</td>
                <td className="p-4">
                  <span className={`text-[10px] uppercase tracking-[0.18em] px-2 py-1 ${
                    i.availability === "Available" ? "bg-green-500/10 text-green-400 border border-green-500/30" :
                    i.availability === "Limited" ? "bg-orange-500/10 text-orange-300 border border-orange-500/30" :
                    "bg-red-500/10 text-red-300 border border-red-500/30"
                  }`}>{i.availability}</span>
                </td>
                <td className="p-4">
                  <button onClick={() => del(i.id)} className="text-neutral-500 hover:text-red-400">
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
